import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import {
  Box,
  TextField,
  Button,
  Card,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import logo from '../../images/logo.png';
import { ReportContext } from '../ReportContext';
import { AuthContext } from '../../auth/AuthContext';
import { showSwalNotification } from '../../utils/SwalResponse';
import MyWebsite from '../../utils/MyWebsite';
import MediaContainer from './util/MediaContainer';

const forestGreen = '#228B22';
const LOCAL_STORAGE_KEY = 'reportFormData';

const ReportIssue = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { authToken } = useContext(AuthContext);
  const { loading, addNewReport, setLoading, addNewUnloggedReport } = useContext(ReportContext);
  const navigate = useNavigate();
  const didRun = useRef(false);

  const [formData, setFormData] = useState({
    description: '',
    category: '',
    website: '',
    media: null,
    media_url: null,
    latitude: null,
    longitude: null,
  });

  // Load from localStorage on mount
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
    if (saved) {
      setFormData((prev) => ({
        ...prev,
        ...saved,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  };

  const getCurrentCoordinates = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) reject(new Error('Geolocation not supported.'));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          resolve({ latitude, longitude });
        },
        (err) => {
          console.error('Geolocation error:', err);
          reject(err);
        }
      );
    });

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    const tryResubmit = async () => {
      const localData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
      if (
        !!authToken &&
        localData?.description &&
        localData?.category &&
        localData?.latitude &&
        localData?.longitude &&
        localData?.media_url
      ) {
        setLoading(true);
        const response = await addNewReport(localData);
        showSwalNotification(response);
        if (response?.status === 'success') {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          navigate(`/reports/${response?.data?.report_id}/details`);
        }
        setLoading(false);
      }
    };
    tryResubmit();
  }, [authToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) {
      setLoading(false);
      Swal.fire('Invalid Input', 'Bot detected.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const position = await getCurrentCoordinates();
      const completeFormData = {
        ...formData,
        latitude: position.latitude,
        longitude: position.longitude,
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(completeFormData));

      if (!authToken) {
        Swal.fire({
          icon: 'info',
          title: 'Login Required',
          text: 'Please log in to submit your report.',
        });
        
        return navigate('/login?rep=submit');
      }

      const response = await addNewReport(completeFormData);

      if (response?.msg) {
        Swal.fire({
          icon: 'info',
          title: 'Session Expired',
          text: 'Please log in again to continue.',
        });
        return navigate('/login?rep=submit');
      }

      showSwalNotification(response);

      if (response?.status === 'success') {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        navigate(`/reports/${response?.data?.report_id}/details`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire('Error', 'An error occurred while submitting the report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUnlogged = async (e) => {
    e.preventDefault();
    if (formData.website) {
      setLoading(false);
      Swal.fire('Invalid Input', 'Bot detected.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const position = await getCurrentCoordinates();
      const completeFormData = {
        ...formData,
        latitude: position.latitude,
        longitude: position.longitude,
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(completeFormData));

      const response = await addNewUnloggedReport(completeFormData);

      showSwalNotification(response);

      if (response?.status === 'success') {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        navigate(`/reports/${response?.data?.report_id}/details`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire('Error', 'An error occurred while submitting the report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- Desktop view (unchanged) ---
  if (isDesktop) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="d-flex justify-content-center align-items-center vh-100 p-4 "
      >
        <Paper elevation={6} sx={{ p: 4, maxWidth: 600, borderRadius: 4, textAlign: 'center' }}>
          <Box mb={2}>
            <img src={logo} alt="App Logo" style={{ maxWidth: 160 }} />
          </Box>
          <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
            Environmental Reporting Form
          </Typography>
          <Typography variant="body1" gutterBottom>
            This form is only available on mobile devices for live geolocation tracking.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please visit this page using your smartphone or tablet. Live location is required to report accurate issue locations.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            size="small"
            sx={{
              mb: 1,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: theme.palette.success.main,
              color: theme.palette.success.main,
              '&:hover': {
                borderColor: theme.palette.warning.dark,
                backgroundColor: 'rgba(34, 139, 38, 0.5)',
              },
            }}
          >
            ← Back
          </Button>
        </Paper>
      </motion.div>
    );
  }

  // --- Mobile (simplified, beautiful UI/UX) ---
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: '100vh', background: 'none' }}
    >
      <motion.div
        initial={{ y: 30, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%', maxWidth: 500, padding: '0 12px' }}
      >
        <Card
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            borderRadius: 5,
            border: `1px solid ${forestGreen}`,
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(4px)',
            position: 'relative',
            boxShadow: '0 20px 40px -12px rgba(34, 139, 34, 0.20)',
          }}
          elevation={4}
        >
          {/* Back button */}
          <Button
            onClick={() => window.history.back()}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12, minWidth: 36, fontSize: '1.3rem', color: '#2b5e2b' }}
          >
            ✕
          </Button>

          {/* Logo */}
          <Box textAlign="center" mb={1}>
            <img src={logo} alt="App Logo" style={{ width: 140, filter: 'drop-shadow(0 4px 8px rgba(0,40,0,0.06))' }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            textAlign="center"
            color={forestGreen}
            fontWeight={700}
            letterSpacing="-0.3px"
            mb={0.5}
          >
            Report Anonymously
          </Typography>

          {/* 🎯 POINTS BADGE – visible when logged in */}
          {authToken && (
            <Box display="flex" justifyContent="center" mt={1} mb={2}>
              <Box
                sx={{
                  bgcolor: forestGreen,
                  color: 'white',
                  borderRadius: 60,
                  px: 3,
                  py: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  boxShadow: '0 6px 16px rgba(34, 139, 34, 0.25)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <span style={{ fontSize: '1.2rem', color: '#ffd966' }}>⭐</span>
                Report & earn <strong style={{ color: '#ffd966', margin: '0 4px' }}>10</strong> redeemable points
              </Box>
            </Box>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Website honeypot (hidden from user) */}
            <MyWebsite onChange={handleChange} />

            {/* Media Container – clean, minimal */}
            <MediaContainer formData={formData} setFormData={setFormData} />

            {/* Description */}
            <TextField
              label="Description (optional)"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
              margin="normal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                  borderColor: '#c0d6c0',
                },
                '& .MuiInputLabel-root': { fontWeight: 500, color: '#2b5e2b' },
              }}
            />

            {/* Category */}
            <FormControl fullWidth margin="normal" sx={{ mt: 1.5 }}>
              <InputLabel sx={{ fontWeight: 500, color: '#2b5e2b' }}>Category</InputLabel>
              <Select
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                sx={{ borderRadius: 4 }}
              >
                {[
                  'Illegal Dumping',
                  'Air Pollution',
                  'Water Pollution',
                  'Soil Pollution',
                  'Littering',
                  'Hazardous Waste Disposal',
                  'Deforestation',
                  'Noise Pollution',
                  'Plastic Pollution',
                  "I don't know ",
                  "Other",
                ].map((cat, i) => (
                  <MenuItem key={i} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Submit Buttons – clean, stacked */}
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.6,
                    backgroundColor: forestGreen,
                    fontWeight: 700,
                    borderRadius: 50,
                    fontSize: '1rem',
                    boxShadow: '0 6px 16px rgba(34, 139, 34, 0.25)',
                    '&:hover': { backgroundColor: '#1b731b' },
                  }}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Report (and earn)'}
                </Button>
              </motion.div>

              <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="button"
                  onClick={handleSubmitUnlogged}
                  variant="outlined"
                  fullWidth
                  sx={{
                    py: 1.6,
                    borderColor: '#b0c8b0',
                    color: '#1e5e1e',
                    fontWeight: 600,
                    borderRadius: 50,
                    borderWidth: 1.5,
                    '&:hover': { backgroundColor: 'rgba(34, 139, 34, 0.04)', borderColor: forestGreen },
                  }}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Report without login'}
                </Button>
              </motion.div>
            </Box>

            {/* Terms */}
            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{ mt: 2.5, color: '#4d6b4d', fontSize: '0.75rem' }}
            >
              By submitting, you agree to our{' '}
              <Link to="/terms" style={{ color: forestGreen, textDecoration: 'underline', fontWeight: 500 }}>
                Terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy" style={{ color: forestGreen, textDecoration: 'underline', fontWeight: 500 }}>
                Privacy Policy
              </Link>.
            </Typography>
          </Box>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default ReportIssue;